export type UserRole = 'user' | 'admin' | 'tribeLeader';
export type UserStatus = 'pending' | 'active' | 'disabled';

/**
 * `open`     — Registrierung und Anmeldung möglich
 * `closed`   — keine neuen Registrierungen, bestehende Konten kommen weiter rein
 * `archived` — Welt stillgelegt: nur Administratoren haben noch Zugriff
 */
export type WorldStatus = 'open' | 'closed' | 'archived';

/** Welt, wie sie öffentlich in Auswahlfeldern erscheint. */
export interface WorldOption {
  key: string;
  name: string;
  status: WorldStatus;
}

export interface World extends WorldOption {
  id: number;
  createdAt: string;
  updatedAt: string;
  counts: {
    total: number;
    pending: number;
    active: number;
    disabled: number;
  };
  /** Basis-URL der Welt auf der Spielplattform, z. B. https://de256.die-staemme.de. */
  syncHost: string | null;
  /** Zeitpunkt des letzten erfolgreichen Sync von Dörfern/Spielern/Stämmen. */
  syncedAt: string | null;
  /** Meldung des letzten fehlgeschlagenen Sync; `null` nach Erfolg. */
  syncError: string | null;
  gameData: {
    villages: number;
    players: number;
    tribes: number;
  };
  /** Substanziell-Schwelle der Off-/Deff-Erkennung (Bauernhofplätze). `null` -- eingebaute Vorgabe verwenden. */
  villageIntelSubstantialBp: number | null;
  /** Standard-Zeitraum (Tage) der Off-/Deff-Erkennung. `null` -- eingebaute Vorgabe verwenden. */
  villageIntelDefaultDays: number | null;
  /** Obergrenzen je Einheitentyp für die Off-Dorf-Truppenhochrechnung. `null` -- eingebaute Vorgabe verwenden. */
  villageIntelMaxAxe: number | null;
  villageIntelMaxLight: number | null;
  villageIntelMaxRam: number | null;
}

/** Ein von der Plattform gemeldeter, live laufender Server. */
export interface DiscoveredWorld {
  key: string;
  host: string;
}

export interface User {
  id: number;
  username: string;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  activatedAt: string | null;
  lastLoginAt: string | null;
  /** `null` heißt weltübergreifend — das trifft nur auf Administratoren zu. */
  world: { id: number; key: string; name: string; status: WorldStatus } | null;
  /**
   * Was vom Usertoken sichtbar bleibt. Der Token selbst kommt genau einmal
   * vom Server — in der Antwort auf das Erzeugen.
   */
  token: { prefix: string; createdAt: string; lastUsedAt: string | null } | null;
  /**
   * Die Spielfigur, mit der dieses Konto in seiner Welt auftritt -- steht
   * erst fest, nachdem die Verifizierung (`gamePlayerVerifiedAt`) einmal
   * erfolgreich war, und lässt sich danach nur noch über eine erneute
   * Verifizierung ändern.
   */
  gamePlayerId: number | null;
  gamePlayerName: string | null;
  /** `null`, solange die aktuelle Spielfigur (noch) nicht verifiziert ist. */
  gamePlayerVerifiedAt: string | null;
  /** Ein laufender Verifizierungsversuch -- `null`, wenn keiner läuft. */
  verification: PlayerVerification | null;
}

/**
 * Zwischenstand einer Spielfigur-Verifizierung: der Code steht bereits fest
 * und muss in den öffentlichen Profiltext des angegebenen Spielers
 * eingetragen werden, bevor sich die Verifizierung bestätigen lässt.
 */
export interface PlayerVerification {
  token: string;
  playerId: number;
  playerName: string;
  startedAt: string;
}

export interface UserStats {
  total: number;
  pending: number;
  active: number;
  disabled: number;
  admins: number;
}

export interface SessionResponse {
  user: User | null;
  csrfToken: string;
}

export interface UserListResponse {
  users: User[];
  stats: UserStats;
  worlds: WorldOption[];
}

export interface UserFilter {
  q?: string;
  status?: UserStatus | '';
  role?: UserRole | '';
  /** Weltkürzel, der Sonderwert `global` oder leer für alle Welten. */
  welt?: string;
}

export const STATUS_LABELS: Record<UserStatus, string> = {
  pending: 'Wartet auf Freischaltung',
  active: 'Freigeschaltet',
  disabled: 'Gesperrt',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  user: 'Mitglied',
  tribeLeader: 'Stammesführer',
  admin: 'Administrator',
};

/**
 * Ob dieses Konto den Rest des Portals nutzen darf -- Administratoren sind
 * von der Verifizierungspflicht ausgenommen, alle anderen brauchen eine
 * verifizierte Spielfigur. Dieselbe Regel wie serverseitig
 * (requireVerifiedPlayer, server/src/api/middleware/auth.ts); für jede neue
 * Fläche in der Oberfläche gilt: mit dieser Funktion sperren, außer es
 * wurde ausdrücklich anders entschieden.
 */
export function isPlayerVerified(user: User): boolean {
  return user.role === 'admin' || user.gamePlayerVerifiedAt !== null;
}

export const WORLD_STATUS_LABELS: Record<WorldStatus, string> = {
  open: 'Offen',
  closed: 'Registrierung geschlossen',
  archived: 'Archiviert',
};

/**
 * Auswahlwert für „kein Welt-Bezug". Er steht nur im Formular; an die API
 * geht dafür gar kein Weltkürzel.
 */
export const GLOBAL_WORLD = '__global__';

/** Merkt die zuletzt gewählte Welt, damit sie beim nächsten Login vorsteht. */
const LAST_WORLD_KEY = 'twtool.lastWorld';

export function readLastWorld(): string {
  try {
    return window.localStorage.getItem(LAST_WORLD_KEY) ?? '';
  } catch {
    return '';
  }
}

export function rememberLastWorld(value: string): void {
  try {
    window.localStorage.setItem(LAST_WORLD_KEY, value);
  } catch {
    // Privater Modus oder blockierter Speicher — die Bequemlichkeit entfällt.
  }
}

/* ---------------------------------------------------------------- */
/* Kampfberichte                                                     */
/* ---------------------------------------------------------------- */

export type UnitKey =
  | 'spear' | 'sword' | 'axe' | 'archer' | 'spy' | 'light'
  | 'marcher' | 'heavy' | 'ram' | 'catapult' | 'knight' | 'snob' | 'militia';

export type UnitCounts = Partial<Record<UnitKey, number>>;

/**
 * Eine Seite des Kampfes. Namen ändern sich im Spiel, die IDs nicht —
 * beide kommen aus dem HTML des Berichts.
 */
export interface ReportSide {
  player: string | null;
  playerId: number | null;
  village: string | null;
  villageId: number | null;
  coordinates: { x: number; y: number } | null;
  units: UnitCounts | null;
  losses: UnitCounts | null;
}

export interface ScoutResources {
  wood: number;
  stone: number;
  iron: number;
}

/** Ein erspähtes Gebäude. `id` ist der technische Schlüssel (`main`, `barracks`, …). */
export interface ScoutBuilding {
  id: string;
  level: number;
}

export interface ScoutData {
  resources: ScoutResources | null;
  buildings: ScoutBuilding[] | null;
}

/** Wallschaden durch Rammböcke -- laut Spielregeln immer der Wall, kein anderes Gebäude. */
export interface WallDamage {
  before: number;
  after: number;
}

/** Gebäudeschaden durch Katapultbeschuss. `building` ist der angezeigte Name aus der Schadenstabelle. */
export interface CatapultDamage {
  building: string;
  before: number;
  after: number;
}

/** Zustimmung des Dorfes -- sinkt sie unter 0, ist das Dorf erobert. */
export interface LoyaltyDamage {
  before: number;
  after: number;
}

/** Truppen des Verteidigers, die in einem seiner anderen Dörfer standen. */
export interface DefenderVillageUnits {
  village: string;
  villageId: number | null;
  /** `null`, falls sich die Koordinaten ausnahmsweise nicht lesen ließen. */
  coordinates: { x: number; y: number } | null;
  units: UnitCounts;
}

/**
 * Größeneinordnung des Angriffs nach der Summe aller mitgeschickten
 * Angreifer-Truppen: klein 0-999, mittel 1000-4999, groß ab 5000.
 */
export type AttackSize = 'small' | 'medium' | 'large';

/**
 * Kampfergebnis aus Sicht der eigenen Spielfigur -- welche Seite "man
 * selbst" ist (Angreifer oder Verteidiger), hängt vom Betrachter ab, nicht
 * vom Bericht. Deshalb kommt `viewerOutcome` bereits fertig ausgewertet vom
 * Server (anhand der hinterlegten Spielfigur), statt hier aus den rohen
 * Verlustzahlen neu berechnet zu werden.
 */
export type ReportOutcomeTier =
  | 'completeVictory'
  | 'victoryWithLosses'
  | 'defeat'
  | 'defeatButScouted'
  | 'scoutSuccess'
  | 'scoutSuccessWithLosses'
  | 'scoutFailed';

export interface ViewerOutcome {
  side: 'attacker' | 'defender';
  outcome: ReportOutcomeTier | null;
}

export interface Report {
  id: number;
  worldId: number;
  worldKey: string;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  /** Betreff des Berichts, wie ihn das Spiel selbst erzeugt. */
  name: string | null;
  attacker: ReportSide;
  defender: ReportSide;
  /**
   * Truppenstärke des Angreifers in Bauernhofplätzen (nicht einfach die
   * Kopfzahl -- eine leichte Kavallerie zählt je nach Welteinstellung z. B.
   * mehrfach so viel wie ein Speerträger); `null` ohne lesbare Truppentabelle.
   */
  attackerUnitCount: number | null;
  /**
   * `null`, wenn weder die eigene Spielfigur noch ein Verbündeter (laut
   * Verwaltung -- verwaltete Spieler/Stämme, `/verwaltung`) am Bericht
   * beteiligt ist. Ist die eigene Spielfigur nicht beteiligt, aber
   * Angreifer/Verteidiger ein Verbündeter, gilt das Ergebnis aus DESSEN
   * Perspektive -- welche Farbe/welches Icon das ergibt, entscheidet ganz
   * normal `outcome`/`OUTCOME_ICON`, keine eigene "Verbündeter"-Kategorie.
   */
  viewerOutcome: ViewerOutcome | null;
  battleAt: string | null;
  luck: number | null;
  /** Gesetzt, wenn der Bericht zusätzlich einen Spionage-Abschnitt enthält. */
  scout: ScoutData | null;
  wallDamage: WallDamage | null;
  catapultDamage: CatapultDamage | null;
  loyaltyDamage: LoyaltyDamage | null;
  defenderUnitsInOtherVillages: DefenderVillageUnits[] | null;
  defenderUnitsAway: UnitCounts | null;
}

/** Dorf-Bezug einer Zeile der "Aktuelle Angriffe"-Übersicht -- Ziel oder Herkunft. */
export interface IncomingAttackVillageRef {
  village: string | null;
  villageId: number | null;
  coordinates: { x: number; y: number } | null;
}

/** Ein einzelnes Ziel eines Dopplers -- ein Eintrag je Angriff aus demselben Herkunftsdorf. */
export interface DopplerTarget {
  village: string | null;
  coordinates: { x: number; y: number } | null;
  arrivesAt: string;
}

/**
 * Ein eingehender Befehl, wie ihn das Userscript automatisch von der
 * "Eingehende Befehle"-Seite hochlädt (`mode=incomings`). Jeder Upload
 * ersetzt vollständig, was zuvor unter diesem Konto gespeichert war --
 * abgelaufene/abgebrochene Befehle verschwinden dadurch von selbst.
 */
export interface IncomingAttack {
  id: number;
  worldId: number;
  worldKey: string;
  createdBy: number;
  /** Konto, das den Befehl hochgeladen hat -- mehrere Kontos tragen zur selben Liste bei. */
  createdByName: string | null;
  commandGameId: number;
  target: IncomingAttackVillageRef;
  origin: IncomingAttackVillageRef;
  attacker: { name: string | null; playerId: number | null };
  /** Besitzer des Zieldorfs, frisch aus den zuletzt synchronisierten Weltdaten aufgelöst; `null` falls unbekannt. */
  defender: { name: string | null; playerId: number | null };
  distance: number | null;
  /**
   * Anzahl der Angriffe aus demselben Herkunftsdorf (über alle Konten/Ziele
   * der Welt) -- ab `2` ein "Doppler": ein gegnerisches Dorf schickt eine
   * echte Off und mehrere Fakes, um zu verschleiern, welcher Angriff die
   * echten Truppen trägt.
   */
  dopplerCount: number;
  /** `null` unter `dopplerCount` 2 -- sonst alle Ziele/Ankunftszeiten der Gruppe, inklusive dieser Zeile. */
  dopplerTargets: DopplerTarget[] | null;
  capturedAt: string;
  arrivesAt: string;
  createdAt: string;
  /**
   * Off-Dorf-Truppenhochrechnung des Herkunftsdorfs ("mögliche Truppen: ~X",
   * siehe server/src/village-intel/troop-projection*.ts) -- `null`, wenn kein
   * Herkunftsdorf bekannt ist. Dieselbe Grundlage wie die Userscript-
   * Statusspalte (Größe-Icon + Deff-Verdacht).
   */
  troopProjection: {
    basis: 'evidence' | 'assumed_full_off';
    projected: Partial<Record<'axe' | 'light' | 'ram', number>>;
    attackSize: AttackSize;
    /** `true`, wenn das Herkunftsdorf laut Off-/Deff-Erkennung als REINES Deff-Dorf gilt. */
    suspectedDeff: boolean;
    /** `true`, wenn das Herkunftsdorf laut Off-/Deff-Erkennung als Off-Dorf gilt (Rolle `off` oder `off_deff`). */
    knownOff: boolean;
  } | null;
}

/**
 * Off-/Deff-Dorf-Erkennung: aus vorhandenen Berichten hergeleitet, welche
 * gegnerischen Dörfer vermutlich Offensiv- bzw. Defensiv-Dörfer sind. Rein
 * heuristisch, kein gespeicherter Zustand -- eine Vorsortierung, kein
 * Ersatz für eigenes Urteilsvermögen.
 */
export type VillageRole = 'off' | 'deff' | 'off_deff' | 'unknown';

export const VILLAGE_ROLE_LABEL: Record<VillageRole, string> = {
  off: 'Off',
  deff: 'Deff',
  off_deff: 'Off + Deff',
  unknown: 'Unbekannt',
};

export interface VillageIntelEvidence {
  reportId: number;
  battleAt: string | null;
  kind:
    | 'off_garrison'
    | 'off_away'
    | 'off_dispatched'
    | 'off_attack_received'
    | 'deff_attack'
    | 'deff_away'
    | 'deff_dispatched';
  bp: number;
}

export const VILLAGE_INTEL_EVIDENCE_LABEL: Record<VillageIntelEvidence['kind'], string> = {
  off_garrison: 'Off-Truppen als Garnison gesehen',
  off_away: 'Off-Truppen extern (in einem anderen Dorf desselben Spielers) gesehen',
  off_dispatched: 'Off-Truppen dieses Dorfs unterwegs gesehen',
  off_attack_received: 'Angriff gegen uns mit substanzieller Off-Menge erhalten',
  deff_attack: 'Angriff mit substanzieller Deff-Menge ausgeführt',
  deff_away: 'Deff-Truppen extern (in einem anderen Dorf desselben Spielers) gesehen',
  deff_dispatched: 'Deff-Truppen dieses Dorfs unterwegs gesehen',
};

export interface VillageIntel {
  villageId: number;
  village: string | null;
  coordinates: { x: number; y: number } | null;
  playerId: number | null;
  playerName: string | null;
  isOff: boolean;
  isDeff: boolean;
  role: VillageRole;
  /** Zeitpunkt des jüngsten Belegs -- Grundlage der Sortierung (frischste Erkenntnis zuerst). */
  latestBattleAt: string | null;
  /** Neueste zuerst. */
  evidence: VillageIntelEvidence[];
  /**
   * Off-Dorf-Truppenhochrechnung ("mögliche Truppen: ~X") -- nur bei
   * `role: 'off'`/`'off_deff'` gesetzt, sonst `null`. `attackSize` nutzt
   * dieselben Schwellen wie `attackSizeFromCount` (klein/mittel/groß nach
   * Bauernhofplätzen) -- dieselben Icons (`ATTACK_SIZE_ICON`) lassen sich
   * dadurch unverändert wiederverwenden.
   */
  troopProjection: {
    basis: 'evidence' | 'assumed_full_off';
    projected: Partial<Record<'axe' | 'light' | 'ram', number>>;
    attackSize: AttackSize;
    elapsedHours: number | null;
  } | null;
}

/** Icon für Angreiferberichte, bei denen zusätzlich Gebäude erspäht wurden. */
export const SCOUT_BUILDINGS_ICON = '/icons/command/spy.webp';
export const SCOUT_BUILDINGS_LABEL = 'Gebäude erspäht';

/** Ob der Bericht erspähte Gebäudestände enthält -- unabhängig vom Betrachter. */
export function hasScoutedBuildings(report: Pick<Report, 'scout'>): boolean {
  return Boolean(report.scout?.buildings && report.scout.buildings.length > 0);
}

/**
 * Eigenes Icon für Angreiferberichte, bei denen ein Adelsgeschlecht (snob)
 * mitgeschickt wurde -- unabhängig vom Späh-Icon: ein Angriff kann Späher
 * UND Adelsgeschlechter gleichzeitig mitführen, beide Icons erscheinen dann
 * nebeneinander.
 */
export const SNOB_ICON = '/icons/command/snob.webp';
export const SNOB_LABEL = 'Adelsgeschlecht eingesetzt';

/** Ob der Angreifer ein Adelsgeschlecht mitgeschickt hat -- unabhängig vom Betrachter. */
export function hasSnob(report: Pick<Report, 'attacker'>): boolean {
  return Boolean(report.attacker.units?.snob && report.attacker.units.snob > 0);
}

/**
 * Die Schwellen (1.000/5.000) sind unverändert aus der Zeit, als hier noch
 * die reine Kopfzahl der Truppen stand -- jetzt kommen Bauernhofplätze rein
 * (siehe `Report.attackerUnitCount`). Ob das für "klein/mittel/groß" noch
 * die richtigen Grenzen sind, muss sich in der Praxis zeigen.
 */
export function attackSizeFromCount(count: number | null): AttackSize | null {
  if (count === null) return null;
  if (count >= 5000) return 'large';
  if (count >= 1000) return 'medium';
  return 'small';
}

export const ATTACK_SIZE_ICON: Record<AttackSize, string> = {
  small: '/icons/attack_small.webp',
  medium: '/icons/attack_medium.webp',
  large: '/icons/attack_large.webp',
};

export const ATTACK_SIZE_LABEL: Record<AttackSize, string> = {
  small: 'Kleiner Angriff (unter 1.000 Bauernhofplätzen)',
  medium: 'Mittelgroßer Angriff (1.000–4.999 Bauernhofplätze)',
  large: 'Großer Angriff (ab 5.000 Bauernhofplätzen)',
};

/**
 * "scoutSuccess" bekommt einen eigenen blauen Punkt -- genau die
 * Farbcodierung, die das Spiel selbst für einen sauberen Spähangriff benutzt
 * ("Erspäht", kein Späher gefallen; an einem echten Bericht geprüft, dessen
 * Icon-Datei `dots/blue.webp` heißt). "scoutSuccessWithLosses" bekommt
 * denselben gelben Punkt wie "victoryWithLosses", "scoutFailed" denselben
 * roten wie "defeat" -- der Titel-Tooltip unterscheidet trotzdem eindeutig
 * zwischen einem Kampf- und einem reinen Späh-Ergebnis.
 */
export const OUTCOME_ICON: Record<ReportOutcomeTier, string> = {
  completeVictory: '/icons/dots/green.webp',
  victoryWithLosses: '/icons/dots/yellow.webp',
  defeat: '/icons/dots/red.webp',
  defeatButScouted: '/icons/dots/red_blue.webp',
  scoutSuccess: '/icons/dots/blue.webp',
  scoutSuccessWithLosses: '/icons/dots/yellow.webp',
  scoutFailed: '/icons/dots/red.webp',
};

/**
 * "defeat" heißt beim Angreifer "Besiegt", beim Verteidiger "Verlust" --
 * dasselbe Ergebnis, nur anders benannt. "defeatButScouted",
 * "scoutSuccess", "scoutSuccessWithLosses" und "scoutFailed" gibt es nur
 * für Angreifer (nur der Angreifer bringt ein Spionage-Ergebnis zurück).
 * Die anderen Stufen sind seitenunabhängig benannt.
 */
export function outcomeLabel(side: 'attacker' | 'defender', outcome: ReportOutcomeTier): string {
  switch (outcome) {
    case 'completeVictory':
      return 'Vollständiger Sieg (keine eigenen Verluste)';
    case 'victoryWithLosses':
      return 'Sieg mit Verlusten (Gegner vollständig aufgerieben)';
    case 'defeat':
      return side === 'attacker'
        ? 'Besiegt (eigene Truppen vollständig aufgerieben)'
        : 'Verlust (eigene Verteidigung vollständig aufgerieben)';
    case 'defeatButScouted':
      return 'Besiegt, aber erspäht (eigene Truppen aufgerieben, Späher kamen trotzdem zurück)';
    case 'scoutSuccess':
      return 'Erspäht (Ergebnis zurückerhalten, kein Späher gefallen)';
    case 'scoutSuccessWithLosses':
      return 'Erspäht, aber mit Verlusten (Ergebnis zurückerhalten, aber Späher gefallen)';
    case 'scoutFailed':
      return 'Spähangriff gescheitert (Späher abgefangen, kein Ergebnis)';
  }
}

export interface InsertOutcome {
  report: Report;
  /** Der Bericht lag schon in dieser Welt — es wurde nichts neu angelegt. */
  duplicate: boolean;
  warnings: string[];
  /** Woher der Bericht kam. */
  source: 'url' | 'raw';
}

/** Ein einzelner Bericht innerhalb eines eingelesenen Stapels. */
export interface BatchItem {
  /** Token oder Link, wie er erkannt wurde. */
  reference: string;
  ok: boolean;
  outcome?: InsertOutcome;
  error?: string;
}

export interface BatchInsertOutcome {
  items: BatchItem[];
  world: { key: string; name: string };
}

export const UNIT_LABELS: Record<UnitKey, string> = {
  spear: 'Speer',
  sword: 'Schwert',
  axe: 'Axt',
  archer: 'Bogen',
  spy: 'Späher',
  light: 'LKav',
  marcher: 'BBogen',
  heavy: 'SKav',
  ram: 'Ramme',
  catapult: 'Kata',
  knight: 'Paladin',
  snob: 'AG',
  militia: 'Miliz',
};

/**
 * Deutsche Namen für die technischen Gebäudeschlüssel aus den erspähten
 * Gebäudeständen (`ScoutBuilding.id`). Ein unbekannter Schlüssel zeigt sich
 * selbst an, statt zu verschwinden -- neue Gebäudetypen brechen die Anzeige
 * so nicht.
 */
export const BUILDING_LABELS: Record<string, string> = {
  main: 'Hauptgebäude',
  barracks: 'Kaserne',
  stable: 'Stall',
  garage: 'Werkstatt',
  snob: 'Adelshof',
  smith: 'Schmiede',
  place: 'Versammlungsplatz',
  market: 'Markt',
  wood: 'Holzfäller',
  stone: 'Lehmgrube',
  iron: 'Eisenmine',
  farm: 'Bauernhof',
  storage: 'Speicher',
  wall: 'Wall',
};

export function buildingLabel(id: string): string {
  return BUILDING_LABELS[id] ?? id;
}

/* ---------------------------------------------------------------- */
/* Abschickzeiten                                                    */
/* ---------------------------------------------------------------- */

/**
 * Ein Zeitfenster, in dem das Konto einen Angriffsplan abschicken könnte.
 * `startAt`/`endAt` sind vollständige Zeitstempel (ISO 8601, UTC) -- Datum
 * und Uhrzeit stecken beide darin, nicht als getrennte Felder. Das macht ein
 * über Mitternacht laufendes Fenster automatisch richtig.
 */
export interface SendWindow {
  id: number;
  startAt: string;
  endAt: string;
  createdAt: string;
  updatedAt: string;
}

/* ---------------------------------------------------------------- */
/* Verwaltung -- verwaltete Spieler und Stämme                       */
/* ---------------------------------------------------------------- */

export type ManagedEntityKind = 'player' | 'tribe';

/**
 * Ein als "verwaltet" markierter Spieler oder Stamm. Gespeichert ist nur die
 * Spiel-ID -- `name`/`tag` kommen frisch aus den zuletzt synchronisierten
 * Weltdaten und sind `null`, wenn die ID dort (z. B. vor der nächsten
 * Synchronisierung) nicht mehr auftaucht.
 */
export interface ManagedEntity {
  id: number;
  kind: ManagedEntityKind;
  gameId: number;
  name: string | null;
  tag: string | null;
  addedByName: string | null;
  createdAt: string;
}

/** Ein Treffer aus den synchronisierten Weltdaten, zur Auswahl beim Hinzufügen. */
export interface GameEntitySearchResult {
  gameId: number;
  name: string;
  tag: string | null;
}

/* ---------------------------------------------------------------- */
/* SD-Anfragen -- Sammeldeff-Paket-Anfragen                          */
/* ---------------------------------------------------------------- */

export type SdRequestStatus = 'pending' | 'approved';

export interface SdRequestContribution {
  id: number;
  userName: string | null;
  amount: number;
  createdAt: string;
}

/**
 * Ein Mitglied bittet für sein eigenes Dorf um eine Anzahl "Pakete" -- was
 * genau ein Paket enthält, ist reine Stamm-Konvention, twtool speichert nur
 * die Anzahl. Erst nach Genehmigung (`status: 'approved'`) durch einen
 * Stammesführer/Administrator ist sie für alle Mitglieder der Welt
 * sichtbar. `packagesRequested` bleibt die ursprüngliche Anfrage,
 * `packagesRemaining` sinkt mit jedem Beitrag -- erreicht sie 0 oder wird
 * negativ, gilt die Anfrage als erfüllt und verschwindet (siehe Server,
 * `sd-request.service.ts`).
 */
export interface SdRequest {
  id: number;
  createdBy: number;
  createdByName: string | null;
  villageX: number;
  villageY: number;
  packagesRequested: number;
  packagesRemaining: number;
  description: string;
  status: SdRequestStatus;
  approvedByName: string | null;
  approvedAt: string | null;
  createdAt: string;
  contributions: SdRequestContribution[];
}

/* ---------------------------------------------------------------- */
/* Retime-Anfragen                                                   */
/* ---------------------------------------------------------------- */

export interface RetimeRequestConfirmation {
  id: number;
  userId: number;
  userName: string | null;
  createdAt: string;
}

/**
 * Ein Mitglied bittet um einen Retime auf ein Dorf zu einer bestimmten
 * Ankunftszeit -- anders als SD-Anfragen OHNE Genehmigungsschritt, sofort
 * für alle Mitglieder der Welt sichtbar. Mitglieder "bestätigen" eine
 * Anfrage, wenn sie selbst einen Retime dorthin schicken, der zu dieser Zeit
 * ankommt -- mehrere Bestätigungen sind ausdrücklich erwünscht.
 */
export interface RetimeRequest {
  id: number;
  createdBy: number;
  createdByName: string | null;
  villageX: number;
  villageY: number;
  arrivalTimestamp: number;
  createdAt: string;
  confirmations: RetimeRequestConfirmation[];
}

/* ---------------------------------------------------------------- */
/* Userskript-Verwaltung                                             */
/* ---------------------------------------------------------------- */

/**
 * Eine hochgeladene Version des Userskripts (ohne den vollen Dateiinhalt --
 * der wird nur beim Download über `/userscript/twtool.user.js`
 * ausgeliefert, siehe server, `userscript.repo.ts`). Hochladen legt einen
 * Entwurf an (`isPublished: false`); erst "Veröffentlichen" macht eine
 * Version zur aktiven.
 */
export interface UserscriptVersion {
  id: number;
  version: string;
  description: string | null;
  uploadedByName: string | null;
  createdAt: string;
  isPublished: boolean;
  publishedAt: string | null;
  publishedByName: string | null;
}

export interface UserscriptLatest {
  version: string;
  publishedAt: string;
}

/* ---------------------------------------------------------------- */
/* Discord-Benachrichtigungen                                        */
/* ---------------------------------------------------------------- */

export type DiscordNotificationType =
  | 'sd_request_created'
  | 'sd_request_approved'
  | 'retime_request_created'
  | 'ag_request_created'
  | 'world_conquest';

/**
 * Ein-/Ausschalter samt Webhook je Welt und Typ. `webhookUrl` selbst kommt
 * NIE vom Server (Zugangsdaten-ähnliches Geheimnis) -- nur `hasWebhookUrl`,
 * ob überhaupt eine hinterlegt ist.
 */
export interface DiscordNotificationSetting {
  type: DiscordNotificationType;
  enabled: boolean;
  hasWebhookUrl: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

/* ---------------------------------------------------------------- */
/* Angriffsplaner                                                    */
/* ---------------------------------------------------------------- */

export type AttackPackageCategory = 'wallbreaker' | 'attack' | 'fake' | 'catapult' | 'noble';

export const ATTACK_PACKAGE_CATEGORY_LABELS: Record<AttackPackageCategory, string> = {
  wallbreaker: 'Wallbreaker',
  attack: 'Angriff',
  fake: 'Fake',
  catapult: 'Katapult',
  noble: 'Adelsgeschlecht',
};

export interface AttackPackageTemplate {
  id: number;
  worldId: number;
  name: string;
  category: AttackPackageCategory;
  minUnits: UnitCounts;
  /** Nur für den DS-Workbench-Export -- `null` heißt: Export nutzt dieselben Zahlen wie `minUnits`. */
  exportUnits: UnitCounts | null;
  catapultTarget: string | null;
  dsWorkbenchType: number | null;
  priority: number;
  /** Nur für `category` 'attack'/'wallbreaker' wirksam -- ein Dorf bekommt dann plan-weit nur EIN Paket dieser Kategorie (Offs/Wallbreaker sollen nie gesplittet werden). */
  preventSplitPerVillage: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AttackPlanMode = 'manual' | 'katta';
export type AttackPlanStatus = 'offen' | 'in_bearbeitung' | 'fertig';

export const ATTACK_PLAN_STATUS_LABELS: Record<AttackPlanStatus, string> = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  fertig: 'Fertig',
};

export interface AttackPlan {
  id: number;
  worldId: number;
  name: string;
  mode: AttackPlanMode;
  wavesPerTarget: number | null;
  fakePackageTemplateId: number | null;
  fakeArrivalStart: number | null;
  fakeArrivalEnd: number | null;
  status: AttackPlanStatus;
  lastCheckedAt: string | null;
  lastCheckCoveragePercent: number | null;
  /** Zuletzt beim Hinzufügen einer Welle benutzte Ankunftszeitspanne -- Vorbelegung fürs nächste Formular. `null`, solange nie eine Welle mit Zeit hinzugefügt wurde. */
  lastWaveArrivalStart: number | null;
  lastWaveArrivalEnd: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AttackPlanWave {
  id: number;
  targetId: number;
  packageTemplateId: number;
  count: number;
  arrivalTimestamp: number;
  arrivalEnd: number | null;
  critical: boolean;
}

export interface AttackPlanTarget {
  id: number;
  planId: number;
  x: number;
  y: number;
  label: string;
  waves: AttackPlanWave[];
}

export interface PoolVillage {
  id: number;
  planId: number;
  x: number;
  y: number;
  label: string;
  player: string | null;
  units: UnitCounts;
}

export interface WaveAssignment {
  x: number;
  y: number;
  label: string;
  player: string | null;
  launchTimestamp: number;
  arrivalTimestamp: number;
}

export interface WaveShortfallReason {
  keineTruppen: number;
  zuWeitWeg: number;
  ausserhalbFenster: number;
  /** Dorf käme in Frage, ist aber wegen Splitting-Schutz oder Fake-Dopplerschutz für dieses Ziel/diese Kategorie bereits verbraucht. */
  bereitsVerwendet: number;
  poolSize: number;
}

export interface WaveResult {
  waveId: number;
  targetId: number;
  packageTemplateId: number;
  critical: boolean;
  requested: number;
  assigned: number;
  assignments: WaveAssignment[];
  error: string | null;
  reason: WaveShortfallReason | null;
}

export interface FakeFillResult {
  targetId: number;
  requested: number;
  assigned: number;
  assignments: WaveAssignment[];
}

export interface FeasibilityResult {
  waves: WaveResult[];
  allSatisfied: boolean;
  droppedCriticalTargets: number[];
  fakeFills: FakeFillResult[];
  lastCheckedAt: string | null;
}

/** Export-Zeilen eines Spielers -- `player: null` sammelt Absender-Dörfer ohne Spielernamen (kein "Player" im CSV-Import). */
export interface DsWorkbenchPlayerGroup {
  player: string | null;
  lines: string[];
}

/** Ergebnis von "Workbenchbefehle nach Spieler gruppieren" -- ein eingefügter, fremder Export statt einer eigenen Planung. */
export interface GroupPastedWorkbenchResult {
  byPlayer: DsWorkbenchPlayerGroup[];
  /** Zeilen, deren erstes Feld keine gültige Dorf-Spiel-ID ist (falsches/kaputtes Format). */
  invalidLines: string[];
  /** Zeilen mit gültigem Format, deren Quelldorf-Spiel-ID der Welt aber unbekannt ist. */
  unknownVillageLines: string[];
}

export interface DsWorkbenchExportResult {
  /** Alle Zeilen in einem Rutsch, für den "Gesamt"-Export. */
  lines: string[];
  /** Dieselben Zeilen nach dem Spieler des Absender-Dorfs gruppiert -- eine Box je beteiligtem Spieler. */
  byPlayer: DsWorkbenchPlayerGroup[];
  skipped: number;
}

/* ---------------------------------------------------------------- */
/* AG-Planer (Stämme-Karte)                                          */
/* ---------------------------------------------------------------- */

export type TerrainStatus =
  | { status: 'ready' }
  | { status: 'loading'; done: number; total: number }
  | { status: 'error'; message: string }
  | { status: 'missing' };

export interface AgPlanerMap {
  world: { key: string; name: string };
  /** Rastergröße der Karte in Kacheln. */
  extent: { width: number; height: number };
  /** [gameId, name, tribeIndex (-1 = ohne Stamm)] */
  players: Array<[number, string, number]>;
  /** [gameId, tag, name] */
  tribes: Array<[number, string, string]>;
  /** [x, y, points, playerIndex (-1 = Barbarendorf), bonus (0/1), name] */
  villages: Array<[number, number, number, number, number, string]>;
  own: { tribeIndexes: number[]; playerIndexes: number[]; selfPlayerIndex: number };
  terrain: TerrainStatus;
  syncedAt: string | null;
}

export interface AgPlanerTerrain {
  width: number;
  height: number;
  tiles: Uint8Array;
}

export interface AgPlanSummary {
  id: number;
  worldId: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
  commandCount: number;
}

export interface AgPlanTypeInfo {
  /** "type:<DS-Workbench-Angriffstyp>" bzw. "unknown". */
  key: string;
  label: string;
  templates: string[];
  color: string;
  count: number;
  /** Alle Vorlagen mit diesem Angriffstyp sind Fakes -- solche Ziele brauchen keine AGs. */
  isFake: boolean;
}

export interface AgPlanDetail {
  plan: { id: number; name: string; createdAt: string; updatedAt: string; createdByName: string | null };
  /** [sourceX, sourceY, targetX, targetY, arrivalMs, typeIndex (in `types`), unit, commandId] */
  commands: Array<[number, number, number, number, number, number, string, number]>;
  types: AgPlanTypeInfo[];
  /** [x, y, Anzahl AGs] */
  nobles: Array<[number, number, number]>;
  /** Verplante AG-Befehle: [id, sourceX, sourceY, targetX, targetY, Anzahl, ankunftMs, abschickMs] */
  plannedNobles: Array<[number, number, number, number, number, number, number, number]>;
}

export interface AgImportResult {
  imported: number;
  unknownVillageLines: string[];
  invalidLines: string[];
}

export type NobleCandidateStatus = 'ok' | 'past' | 'beforeInc';

export interface NobleCandidate {
  x: number;
  y: number;
  name: string;
  owner: string | null;
  /** Noch ungeplante AGs in diesem Dorf. */
  available: number;
  distance: number;
  travelSeconds: number;
  launchMs: number;
  status: NobleCandidateStatus;
  outsideWindow: boolean;
}

export interface NobleCandidatesResult {
  anchor: {
    id: number;
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    arrivalMs: number;
    unit: string;
    launchMs: number | null;
  };
  arrivalMs: number;
  gapMs: number;
  laterCommands: number;
  candidates: NobleCandidate[];
}

export interface NobleExportResult {
  lines: string[];
  byPlayer: Array<{ player: string | null; lines: string[] }>;
  skipped: number;
}

/* ---------------------------------------------------------------- */
/* AG-Abfrage                                                         */
/* ---------------------------------------------------------------- */

export interface AgEntry {
  id: number;
  userId: number;
  /** Anzeigename des Kontos, das den Eintrag angelegt hat. */
  userName: string;
  x: number;
  y: number;
  count: number;
  villageName: string | null;
  updatedAt: string;
}

export interface AgRequestOverview {
  request: { id: number; createdAt: string; createdByName: string | null } | null;
  canManage: boolean;
  mine: AgEntry[];
  /** Alle Einträge der Welt -- nur für Stammesführer/Administratoren, sonst `null`. */
  all: AgEntry[] | null;
}

export interface AgBulkResult {
  saved: number;
  rejected: Array<{ line: string; reason: string }>;
}
